import { i18n } from '@/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { employeesApi } from '@/api/employees.api';
import { approvalsApi } from '@/api/approvals.api';
import { hrApi } from '@/api';
import type { SelectOption } from '@/components/ui/Select';
import type { EmployeeResponse } from '@/types/employee';
import { formatEmployeeShort } from '@/utils/entityLabels';
import { useDebounce } from '@/hooks/useDebounce';
import { showApiError } from '@/utils/apiError';
import {
  buildHrEmployeeSearchParams,
  EMPTY_HR_ORG_FILTERS,
  type HrOrgFilterState,
} from '@/modules/hr/components/hrEmployeeFilterUtils';

function employeeOption(employee: EmployeeResponse): SelectOption {
  return {
    value: String(employee.id),
    label: formatEmployeeShort(employee),
  };
}

function mergeSelectedOption(options: SelectOption[], selected?: SelectOption | null): SelectOption[] {
  if (!selected) return options;
  if (options.some((opt) => opt.value === selected.value)) return options;
  return [selected, ...options];
}

type Params = {
  value?: string;
  activeOnly?: boolean;
  pageSize?: number;
  hrScoped?: boolean;
  excludeEmployeeIds?: number[];
  /** Hide the employee linked to this user id (e.g. current login). */
  excludeUserId?: number;
  /** When true, loads all company employees for approval delegation (not hierarchy-scoped). */
  approvalDelegateScope?: boolean;
  orgFilters?: HrOrgFilterState;
};

/** Server-backed employee search for searchable dropdowns. */
export function useEmployeeSelectSearch({
  value = '',
  activeOnly = true,
  pageSize = 30,
  hrScoped = false,
  excludeEmployeeIds = [],
  excludeUserId,
  approvalDelegateScope = false,
  orgFilters = EMPTY_HR_ORG_FILTERS,
}: Params = {}) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const loadEmployees = useCallback(
    async (search: string) => {
      setSearching(true);
      try {
        const listParams = buildHrEmployeeSearchParams(orgFilters, {
          search: search || undefined,
          page: 0,
          size: pageSize,
          activeOnly,
          sortBy: 'firstName',
          sortDirection: 'ASC' as const,
        });
        const res = approvalDelegateScope
          ? await approvalsApi.delegateCandidates(listParams)
          : hrScoped
            ? await hrApi.listVisibleEmployees(listParams)
            : await employeesApi.list(listParams);
        setEmployees(res.content);
      } catch (err) {
        showApiError(err, i18n.t('errors.failedToLoadEmployees', { ns: 'common' }));
      } finally {
        setSearching(false);
        setLoading(false);
      }
    },
    [activeOnly, approvalDelegateScope, orgFilters, pageSize, hrScoped],
  );

  useEffect(() => {
    void loadEmployees(debouncedSearch);
  }, [debouncedSearch, loadEmployees]);

  useEffect(() => {
    if (!value) {
      setSelectedEmployee(null);
      return;
    }
    const id = Number(value);
    if (Number.isNaN(id)) return;
    const fromList = employees.find((e) => e.id === id);
    if (fromList) {
      setSelectedEmployee(fromList);
      return;
    }
    let cancelled = false;
    const getter = hrScoped ? hrApi.getVisibleEmployee : employeesApi.get;
    getter(id)
      .then((employee) => {
        if (!cancelled) setSelectedEmployee(employee);
      })
      .catch(() => {
        if (!cancelled) setSelectedEmployee(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value, employees, hrScoped]);

  const excluded = useMemo(() => new Set(excludeEmployeeIds), [excludeEmployeeIds]);

  const options = useMemo(() => {
    const base = employees
      .filter((employee) => {
        if (excluded.has(employee.id)) return false;
        if (excludeUserId != null && employee.userId === excludeUserId) return false;
        return true;
      })
      .map(employeeOption);
    const selected =
      selectedEmployee &&
      !excluded.has(selectedEmployee.id) &&
      !(excludeUserId != null && selectedEmployee.userId === excludeUserId)
        ? employeeOption(selectedEmployee)
        : null;
    return mergeSelectedOption(base, selected);
  }, [employees, selectedEmployee, excluded, excludeUserId]);

  const byId = useMemo(() => {
    const map = new Map<number, EmployeeResponse>();
    for (const employee of employees) map.set(employee.id, employee);
    if (selectedEmployee) map.set(selectedEmployee.id, selectedEmployee);
    return map;
  }, [employees, selectedEmployee]);

  const getEmployee = useCallback(
    (id: number | string | null | undefined) => {
      if (id == null || id === '') return undefined;
      return byId.get(Number(id));
    },
    [byId],
  );

  return {
    options,
    loading,
    searching,
    onSearchQuery: setSearchQuery,
    getEmployee,
  };
}
